<template>
    <div v-if="editor" class="container">
        <div class="control-group">
            <div class="btn-editor-group" role="group" aria-label="Basic outlined example">
                <button 
                    v-for="item in buttonGroup"
                    :key="item"
                    class="btn-editor" 
                    :class="{ 'is-active': setBtnData(item).classCondition }" 
                    @click="setBtnData(item).action"
                >
                    {{ item }}
                </button>
            </div>

            <editor-content :editor="editor" />
        </div>
    </div>
</template>

<script>
import StarterKit from "@tiptap/starter-kit";
import { Editor, EditorContent } from "@tiptap/vue-3";
// import { TaskItem, TaskList } from '@tiptap/extension-list'

export default {
    components: {
        EditorContent,
    },

    props: {
        modelValue: {
            type: String,
            default: "",
        },
    },

    emits: ["update:modelValue"],

    data() {
        return {
            editor: null,
            buttonGroup: ["H1", "H2", "H3", "H4", "H5", "Paragraph", "Bold", "ul", "a"],
        };
    },

    watch: {
        modelValue(value) {
            const isSame = this.editor.getHTML() === value;

            // JSON
            // const isSame = JSON.stringify(this.editor.getJSON()) === JSON.stringify(value)

            if (isSame) {
                return;
            }

            this.editor.commands.setContent(value, false);
        },
    },

    mounted() {
        this.editor = new Editor({
            extensions: [
                StarterKit,
            ],
            content: this.modelValue,
            onUpdate: () => {
                // HTML
                this.$emit("update:modelValue", this.editor.getHTML());

                // JSON
                // this.$emit('update:modelValue', this.editor.getJSON())
            },
        });
    },

    beforeUnmount() {
        this.editor.destroy();
    },

    methods: {
        getLevel(id) {
            switch (id) {
            case "H1":
                return { level: 1 };
            case "H2":
                return { level: 2 };
            case "H3":
                return { level: 3 };
            default:
                return undefined;
            }
        },

        setBtnData(id) {
            return {
                action: () => { // Wrap in a function to execute only on click
                    if (!this.editor) return;
                    switch (id) {
                    case "H1":
                        this.editor.chain().focus().toggleHeading({ level: 1 }).run();
                        break;
                    case "H2":
                        this.editor.chain().focus().toggleHeading({ level: 2 }).run();
                        break;
                    case "H3":
                        this.editor.chain().focus().toggleHeading({ level: 3 }).run();
                        break;
                    case "Paragraph":
                        this.editor.chain().focus().setParagraph().run();
                        break;
                    case "Highlight":
                        this.editor.chain().focus().toggleHighlight().run();
                        break;
                    case "Undo":
                        this.editor.chain().focus().undo().run();
                        break;
                    case "Redo":
                        this.editor.chain().focus().redo().run();
                        break;
                    case "Strike":
                        this.editor.chain().focus().toggleStrike().run();
                        break;
                    case "Center":
                        this.editor.chain().focus().setParagraph().run();
                        break;
                    case "Italic":
                        this.editor.chain().focus().toggleItalic().run();
                        break;
                    case "Bold":
                        this.editor.chain().focus().toggleBold().run();
                        break;
                    case "Justify":
                        this.editor.chain().focus().setTextAlign("justify").run();
                        break;
                    }
                },
                classCondition: this.editor?.isActive(id.toLowerCase(), this.getLevel(id.toLowerCase())), // ✅ This prevents unnecessary reactivity loops
            };
        },

    },
};
</script>

<style lang="scss">
.tiptap {
    :first-child {
        margin-top: 1rem;
        min-height: 100px;
        font-size: 0.875rem;
        padding: 10px
    }

    ul,
    ol {
        padding: 0 1rem;
        margin: 1.25rem 1rem 1.25rem 0.4rem;

        li p {
        margin-top: 0.25em;
        margin-bottom: 0.25em;
        }
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        line-height: 1.1;
        margin-top: 2.5rem;
        text-wrap: pretty;
    }

    h1,
    h2 {
    margin-top: 3.5rem;
    margin-bottom: 1.5rem;
    }

    h1 {
        font-size: 1.4rem;
    }

    h2 {
        font-size: 1.2rem;
    }

    h3 {
        font-size: 1rem;
    }

    h4,
    h5,
    h6 {
        font-size: 1rem;
    }

    code {
        background-color: var(--purple-light);
        border-radius: 0.4rem;
        color: var(--black);
        font-size: 0.85rem;
        padding: 0.25em 0.3em;
    }

    pre {
        background: var(--black);
        border-radius: 0.5rem;
        color: var(--white);
        font-family: 'JetBrainsMono', monospace;
        margin: 1.5rem 0;
        padding: 0.75rem 1rem;

        code {
        background: none;
        color: inherit;
        font-size: 0.8rem;
        padding: 0;
        }
    }

    blockquote {
        border-left: 3px solid var(--gray-3);
        margin: 1.5rem 0;
        padding-left: 1rem;
    }

    hr {
        border: none;
        border-top: 1px solid var(--gray-2);
        margin: 2rem 0;
    }

    p.is-editor-empty:first-child::before {
        color: #adb5bd;
        content: "Enter new note here...";
        float: left;
        height: 0;
        pointer-events: none;
    }
}

.btn-editor-group {
    margin-top: 0.5rem;
}

.btn-editor {
    background: none;
    border: none;
    cursor: pointer;
    display: inline-block;
    height: 24px;
    padding: 3px 5px;
    font-size: 0.875rem;
    color: #6c757d;
    outline: none;

    &:hover {
        color: #51585e;
    }
}
</style>